import { useEffect, useSyncExternalStore } from 'react';

/**
 * The Chats SPLIT view's list-column width, remembered per browser. In `split`
 * mode (`useChatsLayout`) the conversation panel takes whatever is left after
 * this — `CHATS_DOCK_WIDTH` in `use-chats-layout.ts` is `100vw - --sidebar-w -
 * this` — so dragging the boundary between the list and the open conversation
 * grows one at the other's expense, exactly like `use-chat-dock-width.ts` does
 * for the floating chat dock elsewhere in the app. Kept as its own store (not
 * reusing that one) because the two panels resize independently and default to
 * different widths.
 *
 * Web-only: on `stacked` (narrower) widths Chats is list→conversation
 * navigation, one screen at a time, with nothing to split — there is nothing
 * to mirror on native, which is always one screen.
 */
const STORAGE_KEY = 'symphony-chats-list-width';

/** Matches the previous fixed CHATS_LIST_WIDTH — the width before anyone has dragged. */
export const CHATS_LIST_DEFAULT_WIDTH = 360;
export const CHATS_LIST_MIN_WIDTH = 280;
export const CHATS_LIST_MAX_WIDTH = 560;

/** Keep at least this much of the transcript visible beside the list. */
const MIN_VISIBLE_CHAT = 420;

/** The rail's live width, which AppFrame mirrors onto :root as --sidebar-w. */
function railWidth(): number {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return 0;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w');
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function clampChatsListWidth(width: number): number {
  if (!Number.isFinite(width)) return CHATS_LIST_DEFAULT_WIDTH;
  const viewportCap =
    typeof window !== 'undefined'
      ? Math.max(CHATS_LIST_MIN_WIDTH, window.innerWidth - railWidth() - MIN_VISIBLE_CHAT)
      : CHATS_LIST_MAX_WIDTH;
  return Math.min(CHATS_LIST_MAX_WIDTH, viewportCap, Math.max(CHATS_LIST_MIN_WIDTH, Math.round(width)));
}

// SSR and the first client render both read the default, so the server HTML and
// the hydrated tree agree; the remembered value is applied in an effect after mount.
let current = CHATS_LIST_DEFAULT_WIDTH;
let hydrated = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function setChatsListWidth(width: number): void {
  const next = clampChatsListWidth(width);
  if (next === current) return;
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(current));
  } catch {
    // private mode / storage disabled — the width just won't persist.
  }
  emit();
}

export function resetChatsListWidth(): void {
  setChatsListWidth(CHATS_LIST_DEFAULT_WIDTH);
}

function hydrateOnce(): void {
  if (hydrated) return;
  hydrated = true;
  let stored: number | null = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    stored = raw == null ? null : clampChatsListWidth(Number(raw));
  } catch {
    stored = null;
  }
  // A viewport narrower than the remembered width re-clamps it down.
  const next = clampChatsListWidth(stored ?? current);
  if (next !== current) {
    current = next;
    emit();
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-only reset of the in-memory store. */
export function __resetChatsListWidthStore(): void {
  current = CHATS_LIST_DEFAULT_WIDTH;
  hydrated = false;
}

export function useChatsListWidth(): number {
  const width = useSyncExternalStore(subscribe, () => current, () => CHATS_LIST_DEFAULT_WIDTH);
  useEffect(() => {
    hydrateOnce();
    // Keep the width sane if the window shrinks below it.
    const onResize = () => setChatsListWidth(current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}
